// src/theme.js
import { createTheme } from '@mui/material/styles';

// Warm shadow helper using near-black brown instead of cold gray
const warmShadow = (elevation) => {
  const shadows = {
    1: '0 2px 8px rgba(26,20,16,0.06)',
    2: '0 3px 12px rgba(26,20,16,0.07)',
    3: '0 4px 16px rgba(26,20,16,0.08)',
    4: '0 4px 24px rgba(26,20,16,0.08)',
    8: '0 12px 40px rgba(26,20,16,0.12)',
    16: '0 20px 60px rgba(26,20,16,0.14), 0 4px 12px rgba(26,20,16,0.08)',
    24: '0 24px 80px rgba(26,20,16,0.16)',
  };
  return shadows[elevation] || 'none';
};

const defaultShadows = [
  'none',
  warmShadow(1),
  warmShadow(2),
  warmShadow(3),
  warmShadow(4),
  '0 5px 28px rgba(26,20,16,0.09)',
  '0 6px 32px rgba(26,20,16,0.09)',
  '0 7px 36px rgba(26,20,16,0.10)',
  warmShadow(8),
  '0 9px 40px rgba(26,20,16,0.11)',
  '0 10px 44px rgba(26,20,16,0.11)',
  '0 11px 48px rgba(26,20,16,0.11)',
  '0 12px 52px rgba(26,20,16,0.12)',
  '0 13px 56px rgba(26,20,16,0.12)',
  '0 14px 60px rgba(26,20,16,0.12)',
  '0 15px 64px rgba(26,20,16,0.13)',
  warmShadow(16),
  '0 17px 68px rgba(26,20,16,0.14)',
  '0 18px 72px rgba(26,20,16,0.14)',
  '0 19px 76px rgba(26,20,16,0.14)',
  '0 20px 80px rgba(26,20,16,0.14)',
  '0 21px 84px rgba(26,20,16,0.15)',
  '0 22px 88px rgba(26,20,16,0.15)',
  '0 23px 92px rgba(26,20,16,0.15)',
  warmShadow(24),
];

const theme = createTheme({
  palette: {
    primary: {
      main: '#E8891A',
      light: '#F5A94E',
      dark: '#C46F0C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2D6A4F',
      light: '#52B788',
      dark: '#1B4332',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FDFAF5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1410',
      secondary: '#5C4F3D',
      disabled: '#8A7968',
    },
    divider: '#EDE8DF',
    success: {
      main: '#2D6A4F',
      light: '#52B788',
    },
    error: {
      main: '#E11D48',
    },
  },

  typography: {
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 900,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      lineHeight: 1.35,
    },
    h5: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    body1: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    overline: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.12em',
      fontSize: '0.7rem',
    },
    caption: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      lineHeight: 1.5,
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: defaultShadows,

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes pulseGlow {
          0%   { box-shadow: 0 0 0 0 rgba(232,137,26,0.4); }
          70%  { box-shadow: 0 0 0 12px rgba(232,137,26,0); }
          100% { box-shadow: 0 0 0 0 rgba(232,137,26,0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes strikethroughAnim {
          to { transform: translateY(-50%) translateX(0); }
        }
        * {
          box-sizing: border-box;
        }
        body {
          background-color: #FDFAF5;
        }
      `,
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 100,
          textTransform: 'none',
          fontWeight: 700,
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #E8891A 0%, #C46F0C 100%)',
          boxShadow: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #F5A94E 0%, #E8891A 100%)',
            boxShadow: '0 8px 24px rgba(232,137,26,0.4)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: 'none',
          },
        },
        outlinedPrimary: {
          borderColor: '#E8891A',
          borderWidth: '1.5px',
          color: '#E8891A',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(232,137,26,0.06)',
            transform: 'translateY(-1px)',
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1rem',
        },
        sizeMedium: {
          padding: '10px 24px',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8rem',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 24px rgba(26,20,16,0.08)',
          border: '1px solid #EDE8DF',
          borderRadius: 20,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(26,20,16,0.12)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: '1px solid #EDE8DF',
          boxShadow: '0 20px 60px rgba(26,20,16,0.14), 0 4px 12px rgba(26,20,16,0.08)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 700,
          backgroundColor: '#FDFAF5',
          borderBottom: '1px solid #EDE8DF',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: '#FDFAF5',
          borderTop: '1px solid #EDE8DF',
          padding: '12px 24px',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FDFAF5',
          borderRight: '1px solid #EDE8DF',
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          border: '1px solid #EDE8DF',
          boxShadow: 'none',
          marginBottom: 8,
          '&::before': {
            display: 'none',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(232,137,26,0.04)',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: '#E8891A',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E8891A',
            },
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          border: '1px solid #EDE8DF',
          boxShadow: '0 2px 8px rgba(26,20,16,0.06)',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginLeft: 8,
          marginRight: 8,
          '&.Mui-selected': {
            backgroundColor: 'rgba(232,137,26,0.08)',
            borderLeft: '3px solid #E8891A',
            '&:hover': {
              backgroundColor: 'rgba(232,137,26,0.12)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(232,137,26,0.04)',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 600,
          fontSize: '0.75rem',
          borderRadius: 100,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700,
            fontFamily: '"Plus Jakarta Sans", sans-serif',
          },
        },
      },
    },
  },
});

export default theme;
